import { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import StatCard from '../../ui/statCard/statCard.component';
import ServiceDistributionCharts from '../serviceDistributionCharts/serviceDistributionCharts.component';
import CustomDataGrid from '../../ui/customDataGrid/customDataGrid.component';
import EquipmentServiceHistoryGrid from '../equipmentServiceHistoryGrid/equipmentServiceHistoryGrid.component';
import {
  BREAKDOWN_JOB_ISSUES_ENDPOINT,
  BREAKDOWN_JOB_SERVICES_ENDPOINT,
  BREAKDOWN_TECHNICIANS_ENDPOINT,
  BREAKDOWN_USED_PARTS_ENDPOINT,
  EQUIPMENTS_ENDPOINT,
} from '../../../consts';
import { EquipmentFullServiceStats } from '../../../types/analytics';
import { DateRangeValue } from '../../ui/dateRangeFilter/dateRangeFilter.component';
import { useCRUD } from '../../../api/crud';
import { buildUsedPartsBreakdownColumns, formatUsedPartsTotals } from '../../../utils/partDisplay';
import { buildJobServiceValueColumn, formatJobServicesTotals } from '../../../utils/jobServiceDisplay';
import { formatCounterValue } from '../../../utils';

interface EquipmentServiceStatsContentProps {
  equipmentId: string | number;
  stats: EquipmentFullServiceStats;
  dateRange: DateRangeValue;
}

export default function EquipmentServiceStatsContent({
  equipmentId,
  stats,
  dateRange,
}: EquipmentServiceStatsContentProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { get: getEquipment } = useCRUD(EQUIPMENTS_ENDPOINT);
  // undefined = not loaded yet, null = loaded but kind unknown (e.g. fetch failed)
  const [equipmentKind, setEquipmentKind] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;

    const fetchEquipment = async () => {
      const { data } = await getEquipment(equipmentId);
      if (!cancelled) {
        setEquipmentKind(data?.kind ?? null);
      }
    };

    fetchEquipment();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentId]);

  const scopeParams = useMemo(
    () => ({
      equipment_id: equipmentId,
      start_date: dateRange.start_date,
      end_date: dateRange.end_date,
    }),
    [equipmentId, dateRange.start_date, dateRange.end_date]
  );
  const scopeKey = `${equipmentId}-${dateRange.start_date}-${dateRange.end_date}`;

  const technicianColumns: GridColDef[] = [
    { field: 'name', headerName: t('Technician'), flex: 1, minWidth: 160 },
    { field: 'assigned_jobs_count', headerName: t('Assigned Jobs'), width: 150 },
  ];

  const jobIssueColumns: GridColDef[] = [
    { field: 'title', headerName: t('Issue'), flex: 1, minWidth: 160 },
    { field: 'count', headerName: t('Count'), width: 120 },
  ];

  const jobServiceColumns: GridColDef[] = [
    { field: 'title', headerName: t('Service'), flex: 1, minWidth: 160 },
    { field: 'count', headerName: t('Count'), width: 120 },
    buildJobServiceValueColumn(t),
  ];

  const usedPartColumns = buildUsedPartsBreakdownColumns(t);

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
          gap: 2,
        }}
      >
        <StatCard label={t('Jobs in Selected Period')} value={stats.jobs_in_range} />
        <StatCard
          label={t('Used Parts')}
          value={formatUsedPartsTotals(stats.used_parts)}
        />
        <StatCard label={t('Job Issues')} value={stats.job_issues_count} />
        <StatCard
          label={t('Job Services')}
          value={formatJobServicesTotals(stats.job_services_count, stats.job_services_value)}
        />
        <StatCard label={t('Black pages')} value={formatCounterValue(stats.black_pages)} />
        {equipmentKind === 'C' && (
          <StatCard label={t('Color pages')} value={formatCounterValue(stats.color_pages)} />
        )}
        <StatCard label={t('Scanned pages')} value={formatCounterValue(stats.scanned_pages)} />
      </Box>

      <ServiceDistributionCharts stats={stats} hasColorEquipment={equipmentKind === 'C'} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 2,
        }}
      >
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('Most Common Technicians')}
            </Typography>
            <CustomDataGrid
              key={scopeKey}
              url={BREAKDOWN_TECHNICIANS_ENDPOINT}
              columns={technicianColumns}
              extraParams={scopeParams}
              onRowClick={(id) => navigate(`/service/statistics/technicians/${id}`)}
              dataGridProps={{ autoHeight: true, pageSizeOptions: [5, 10, 25], disableColumnFilter: true }}
            />
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('Used Parts')}
            </Typography>
            <CustomDataGrid
              key={scopeKey}
              url={BREAKDOWN_USED_PARTS_ENDPOINT}
              columns={usedPartColumns}
              extraParams={scopeParams}
              dataGridProps={{ autoHeight: true, pageSizeOptions: [5, 10, 25], disableColumnFilter: true }}
            />
          </CardContent>
        </Card>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 2,
        }}
      >
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('Job Issues')}
            </Typography>
            <CustomDataGrid
              key={scopeKey}
              url={BREAKDOWN_JOB_ISSUES_ENDPOINT}
              columns={jobIssueColumns}
              extraParams={scopeParams}
              dataGridProps={{ autoHeight: true, pageSizeOptions: [5, 10, 25], disableColumnFilter: true }}
            />
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('Job Services')}
            </Typography>
            <CustomDataGrid
              key={scopeKey}
              url={BREAKDOWN_JOB_SERVICES_ENDPOINT}
              columns={jobServiceColumns}
              extraParams={scopeParams}
              dataGridProps={{ autoHeight: true, pageSizeOptions: [5, 10, 25], disableColumnFilter: true }}
            />
          </CardContent>
        </Card>
      </Box>

      {/* One row per task done on this machine (not per job), so the parts
          and issues shown are the ones that actually concerned it. */}
      <EquipmentServiceHistoryGrid
        equipmentId={equipmentId}
        title={t('Service History')}
        dateRange={dateRange}
        variant="card"
      />
    </>
  );
}
